/**
 Copyright Highway9 Networks Inc.
 */
import { Device } from "~/types/device";
import { DeviceInfo, DeviceInfoOverride } from "~/types/deviceInfo";
import { DeviceReport, DeviceReportQuery } from "~/types/deviceReport";
import APIService, { MetricsQuery } from "./APIServices";
import { authenticationService } from "./authentication-service";
import { DeviceGroup } from "~/types/deviceGroup";
import { apiFetch } from "~/sw";
import { FlowRecord } from "~/types/FlowRecord";

const deviceAPI = new APIService<Device>("devices");
const deviceInfoAPI = new APIService<DeviceInfo>("deviceInfo");
const deviceGroupAPI = new APIService<DeviceGroup>("deviceGroups");

export const subscriberService = {
  getSubscribers: async () => await apiFetch("fetchDevices", authenticationService.currentUserValue!.token),
  getSubscribersDeviceInfo: deviceInfoAPI.getAll,
  refreshSubscriberDeviceInfo,
  getVmcDeviceInfoSelections,
  updateVmcDeviceInfoSelection,
  deleteVmcDeviceInfoSelection,
  updateDeviceGroupInfoSelection,
  deleteDeviceGroupInfoSelection,
  updateDeviceInfoOverride,
  deleteDeviceInfoOverride,
  createSubscriber: deviceAPI.create,
  updateSubscriber: deviceAPI.update,
  deleteDevice: deviceAPI.delete,
  getMetrics: async (query: MetricsQuery) => await apiFetch("fetchDeviceMetrics", query, authenticationService.currentUserValue!.token),
  getDeviceReport,
  updateSubscribersBulk: (devices: Device[]) => deviceAPI.put('bulk', devices),
  importSubscribers,
  getDeviceGroups: deviceGroupAPI.getAll,
  addDeviceGroup: deviceGroupAPI.create,
  updateDeviceGroup: deviceGroupAPI.update,
  deleteDeviceGroup: deviceGroupAPI.delete,
  getPingmonData,
  getLatestMetricSync,
  getFlowRecords
};

/** POST /devices/report — pre-bucketed usage + connectivity grids for the UE report UI. */
async function getDeviceReport(query: DeviceReportQuery): Promise<DeviceReport[]> {
  return (await deviceAPI.post("report", query)) as unknown as DeviceReport[];
}

function importSubscribers(file: File) {
  const formData = new FormData();
  formData.append("upfile", file);

  const requestOptions = {
    method: "POST",
    headers: { accept: "*/*", Authorization: "Bearer " + authenticationService.currentUserValue?.token },
    body: formData,
  };

  return fetch(`/api/v1/orch/devices/operations/import`, requestOptions);
}

async function refreshSubscriberDeviceInfo(deviceId: string) {
  const response = await fetch(`/api/v1/orch/devices/${encodeURIComponent(deviceId)}/operations/refresh-device-info`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authenticationService.currentUserValue!.token}`,
    },
  });

  const text = await response.text();
  const result = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    throw result;
  }

  return result?.data ?? result;
}

async function updateDeviceInfoOverride(deviceId: string, override: DeviceInfoOverride) {
  return requestDeviceInfoSelection(
    `/api/v1/orch/devices/${encodeURIComponent(deviceId)}/deviceInfo/override`,
    "PUT",
    override
  );
}

async function deleteDeviceInfoOverride(deviceId: string) {
  return requestDeviceInfoSelection(
    `/api/v1/orch/devices/${encodeURIComponent(deviceId)}/deviceInfo/override`,
    "DELETE"
  );
}

async function getVmcDeviceInfoSelections(): Promise<Record<string, string>> {
  return requestDeviceInfoSelection("/api/v1/orch/deviceInfo/selections", "GET");
}

async function updateVmcDeviceInfoSelection(tac: string, override: DeviceInfoOverride) {
  return requestDeviceInfoSelection(
    `/api/v1/orch/deviceInfo/selections/${encodeURIComponent(tac)}`,
    "PUT",
    override
  );
}

async function deleteVmcDeviceInfoSelection(tac: string) {
  return requestDeviceInfoSelection(
    `/api/v1/orch/deviceInfo/selections/${encodeURIComponent(tac)}`,
    "DELETE"
  );
}

async function updateDeviceGroupInfoSelection(groupId: string, tac: string, override: DeviceInfoOverride) {
  return requestDeviceInfoSelection(
    `/api/v1/orch/deviceGroups/${encodeURIComponent(groupId)}/deviceInfo/selections/${encodeURIComponent(tac)}`,
    "PUT",
    override
  );
}

async function deleteDeviceGroupInfoSelection(groupId: string, tac: string) {
  return requestDeviceInfoSelection(
    `/api/v1/orch/deviceGroups/${encodeURIComponent(groupId)}/deviceInfo/selections/${encodeURIComponent(tac)}`,
    "DELETE"
  );
}

async function requestDeviceInfoSelection(url: string, method: string, body?: DeviceInfoOverride) {
  const response = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authenticationService.currentUserValue?.token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const responseBody = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw responseBody;
  }
  return responseBody?.data ?? responseBody;
}

async function getPingmonData(query : MetricsQuery, fullSync : boolean, oldRawData : any[]){
  return await apiFetch("fetchPingmonData", query, fullSync, oldRawData,  authenticationService.currentUserValue!.token)
}

async function getLatestMetricSync(deviceIds: string[], metrics?: string[]) {
  return await apiFetch(
    "fetchLatestMetricSync",
    deviceIds,
    authenticationService.currentUserValue!.token,
    metrics,
  )
}

async function getFlowRecords(deviceId: string) {
  return await deviceAPI.get<FlowRecord[]>(`${deviceId}/flows`)
}
